import CustomerPartNumberForm from "@auer-steel/CustomComponents/MyPartNumbers/CustomerPartNumberForm";
import { MyPartNumbersPageContext } from "@auer-steel/Pages/MyPartNumbersPage";
import { CustomerPartNumber } from "@auer-steel/Services/AuerSteelApiModels";
import { AuerState } from "@auer-steel/Store/AuerState";
import createCustomerPartNumber from "@auer-steel/Store/Data/CustomerPartNumbers/Handlers/CreateCustomerPartNumber";
import clearSearch from "@auer-steel/Store/Pages/MyPartNumbers/Handlers/ClearSearch";
import resetCustomerPartNumberData from "@auer-steel/Store/Pages/MyPartNumbers/Handlers/ResetCustomerPartNumbers";
import toggleFiltersOpen from "@auer-steel/Store/Pages/MyPartNumbers/Handlers/ToggleFiltersOpen";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapDispatchToProps = {
    createCustomerPartNumber,
    toggleFiltersOpen,
    clearSearch,
    resetCustomerPartNumberData,
};

const mapStateToProps = (state: AuerState) => {
    return {
        isSubmitting: state.myPartNumbers.isSubmitting
    };
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CreateCustomerPartNumberButtonStyles {
    container?: InjectableCss;
    createNewCustomerPartNumberButton?: ButtonPresentationProps;
    customerPartNumberFormModal?: ModalPresentationProps;
    toggleFilterIcon?: IconPresentationProps;
    toggleFilterClickable?: ClickablePresentationProps;
}

export const createCustomerPartNumberButtonStyles: CreateCustomerPartNumberButtonStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 12px;
        `,
    },
    customerPartNumberFormModal: {
        cssOverrides: {
            modalContent: css`
                min-height: 400px;
            `,
        },
    },
    toggleFilterIcon: {
        src: Filter,
        size: 24,
    },
};

const styles = createCustomerPartNumberButtonStyles;

const MyPartNumbersActionButtons: React.FunctionComponent<Props> = (props: Props) => {
    const defaultCustomerPartNumberState = {
        id: "",
        product: {},
        customerPartNumbers: [""],
    } as any;
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [newCustomerPartNumber, setNewCustomerPartNumber] = React.useState(defaultCustomerPartNumberState);

    const modalCloseHandler = () => {
        props.resetCustomerPartNumberData();
        setModalIsOpen(false);
    };

    const editClickHandler = () => {
        setModalIsOpen(true);
    };

    const formCancelHandler = (e: any) => {
        setNewCustomerPartNumber(defaultCustomerPartNumberState);
        e.preventDefault();
        modalCloseHandler();
    };

    const formSubmitHandler = (e: any, customerPartNumber: CustomerPartNumber) => {
        props.clearSearch();
        modalCloseHandler();
    };

    return (
        <StyledWrapper {...styles.container}>
            <Button {...styles.createNewCustomerPartNumberButton} onClick={editClickHandler}>
                {translate("Create Part Numbers")}
            </Button>
            <Modal
                headline={translate("Create Part Numbers")}
                {...styles.customerPartNumberFormModal}
                isOpen={modalIsOpen}
                isCloseable={!props.isSubmitting}
                handleClose={modalCloseHandler}
            >
                <CustomerPartNumberForm
                    formType="create"
                    customerPartNumber={newCustomerPartNumber}
                    onSubmit={formSubmitHandler}
                    onCancel={formCancelHandler}
                    isSubmitting={props.isSubmitting}
                />
            </Modal>
            <Clickable
                {...styles.toggleFilterClickable}
                onClick={props.toggleFiltersOpen}
                data-test-selector="myPartNumbers_showHideFilters"
            >
                <Icon {...styles.toggleFilterIcon} />
            </Clickable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyPartNumbersActionButtons),
    definition: {
        group: "My Part Numbers" as any,
        displayName: "Action Buttons",
        icon: "Button",
        allowedContexts: [MyPartNumbersPageContext],
    },
};

export default widgetModule;
